import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { isValidStashItem, COMMENT_POW } from '@/lib/stashstr';
import { verifyPow } from '@/lib/pow';

/** Fetches a single stashstr post by event id. */
export function useItem(rootId: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['stashstr', 'item', rootId ?? ''],
    queryFn: async (c) => {
      if (!rootId) throw new Error('No id');
      const [event] = await nostr.query([{ ids: [rootId], limit: 1 }], { signal: c.signal });
      return event;
    },
    staleTime: 30_000,
    enabled: Boolean(rootId),
  });
}

export interface CommentEntry {
  event: NostrEvent;
  powValid: boolean;
}

/**
 * Fetches flat comments (kind 1111) for a root post.
 * NOTE: relays index lowercase tags only; uppercase filters (#E) are
 * best-effort for NIP-22 compat. The lowercase #e filter carries the load.
 */
export function useItemComments(rootId: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['stashstr', 'comments', rootId ?? ''],
    queryFn: async (c) => {
      if (!rootId) throw new Error('No id');
      const results = await nostr.query(
        [{ kinds: [1111], '#E': [rootId], limit: 100 }, { kinds: [1111], '#e': [rootId], limit: 100 }],
        { signal: c.signal },
      );

      // De-duplicate by id, require K tag for validity
      const byId = new Map<string, NostrEvent>();
      for (const ev of results) {
        const hasRoot = ev.tags.some(([t, v]) => (t === 'E' || t === 'e') && v === rootId);
        const hasKind = ev.tags.some(([t, v]) => t === 'K' && v === '11');
        if (hasRoot && hasKind && !byId.has(ev.id)) {
          byId.set(ev.id, ev);
        }
      }

      const entries: CommentEntry[] = [...byId.values()].map((event) => ({
        event,
        powValid: verifyPow(event, COMMENT_POW),
      }));

      // Proven first (chronological), unproven after
      const proven = entries.filter((e) => e.powValid).sort((a, b) => a.event.created_at - b.event.created_at);
      const unproven = entries
        .filter((e) => !e.powValid)
        .sort((a, b) => a.event.created_at - b.event.created_at);

      return { entries: [...proven, ...unproven], hiddenUnprovenCount: unproven.length };
    },
    staleTime: 15_000,
    enabled: Boolean(rootId),
  });
}
